import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Address, AddressUtil, EthUnitUtil } from '@31third/common';
import { lastValueFrom } from 'rxjs';
import {
  AssetService,
  ChainRepository,
  EnzymeAsset,
  SignerRepository,
} from 'common';
import {
  EnzymeRepository,
  RebalancingRepository,
  SettingsRepository,
} from '../repository';
import { BuyEntry, EnzymeVault, SellEntry } from '../model';
import { EnzymeVaultDto } from '../dto';
import { EnzymeVaultFactory } from '../factory';
import { RebalancingType } from '../enum';
import { RebalancingStep } from '../component';
import { APP_CONFIG } from 'app-config';
import { EnzymeGasRelayerService } from './enzyme-gas-relayer.service';

@Injectable({
  providedIn: 'root',
})
export class EnzymeService {
  constructor(
    @Inject(APP_CONFIG) private appConfig: any,
    private readonly httpClient: HttpClient,
    private readonly enzymeRepository: EnzymeRepository,
    private readonly settingsRepository: SettingsRepository,
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly signerRepository: SignerRepository,
    private readonly chainRepository: ChainRepository,
    private readonly assetService: AssetService,
    private readonly enzymeGasRelayerService: EnzymeGasRelayerService,
  ) {
    this.setupSignerAddressSubscription();
    this.subscribeToSelectedVault();
  }

  private setupSignerAddressSubscription(): void {
    this.signerRepository.address$.subscribe(async address => {
      if (address) {
        await this.loadVaults(address);
      }
    });
  }

  private subscribeToSelectedVault(): void {
    // for now just a workaround if user clears vault selection
    this.enzymeRepository.selectedVault$.subscribe(vault => {
      if (!vault) {
        if (
          this.signerRepository.store.getValue().connected &&
          this.signerRepository.store.getValue().address
        ) {
          void this.assetService.loadAssetsForWallet(
            this.signerRepository.store.getValue().address!,
          );
        } else {
          void this.assetService.loadAssetsGeneral();
        }
      }
    });
  }

  private getBaseUrl(): string {
    return `${this.appConfig.tradingApiBaseUrl}/enzyme`;
  }

  private async loadVaults(connectedSigner: Address): Promise<void> {
    if (
      connectedSigner !== this.enzymeRepository.store.getValue().connectedSigner
    ) {
      this.enzymeRepository.setVaults([], false);
    }

    const dtos = await lastValueFrom(
      this.httpClient.get<EnzymeVaultDto[]>(
        `${this.getBaseUrl()}/vaults?owner=${connectedSigner}&testnet=${
          this.settingsRepository.getActiveChainSetting()?.enzymeTestnet
        }`,
      ),
    );

    const vaults = dtos.map(dto =>
      EnzymeVaultFactory.createFromDto(
        dto,
        this.chainRepository.store.getValue().chain,
      ),
    );
    this.addSettingsVaultIfSet(vaults);

    try {
      for (const vault of vaults) {
        vault.isRelayerEnabled =
          await this.enzymeGasRelayerService.isRelayerEnabled(vault.address);
      }
    } catch (e) {
      // empty on purpose
    }

    this.enzymeRepository.setVaults(vaults, true);
  }

  private addSettingsVaultIfSet(vaults: EnzymeVault[]): void {
    const settingsVault: Address | undefined =
      this.settingsRepository.getActiveChainSetting()?.enzymeVault;
    if (
      settingsVault &&
      !vaults.find(vault => AddressUtil.equals(vault.address, settingsVault))
    ) {
      vaults.push(
        EnzymeVaultFactory.createPlaceholder(
          settingsVault,
          this.chainRepository.store.getValue().chain,
          true,
        ),
      );
    }
  }

  public async selectVaultByAddress(address: Address): Promise<boolean> {
    const vault = this.enzymeRepository.store
      .getValue()
      .vaults.find(vault => vault.address === address);

    if (!vault) {
      return false;
    }

    if (this.enzymeRepository.store.getValue().selectedVault === vault) {
      return true;
    }

    await this.reloadVault(vault);
    return true;
  }

  public async selectVault(vault: EnzymeVault): Promise<void> {
    if (this.enzymeRepository.store.getValue().selectedVault === vault) {
      return;
    }

    await this.reloadVault(vault);
  }

  /**
   * Loaded vaults are just vaults where the connected wallet is owner/manager.
   * This can be used to simulate rebalancings for external owned vaults.
   * @param address
   */
  public createAndSelectVault(address: Address): Promise<void> {
    const vault = EnzymeVaultFactory.createPlaceholder(
      address,
      this.chainRepository.store.getValue().chain,
    );

    return this.reloadVault(vault);
  }

  public async reloadVault(vault: EnzymeVault): Promise<void> {
    this.enzymeRepository.setSelectedVault(vault);
    this.rebalancingRepository.reset();
    this.rebalancingRepository.setType(RebalancingType.VAULT);
    void this.setOwnedByConnectedSigner(vault);
    void this.setOwner(vault);
    void this.setCanConnectedSignerManageAssets(vault);
    const assets = await this.assetService.loadAssetsForEnzymeVault(
      vault.address,
      !!this.settingsRepository.getActiveChainSetting()?.enzymeTestnet,
    );
    this.prefillBuyEndSellEntries(assets);

    // again set selected vault to also store data loaded after inital set
    this.enzymeRepository.setSelectedVault(vault);
  }

  private async setOwnedByConnectedSigner(vault: EnzymeVault): Promise<void> {
    const address = this.signerRepository.store.getValue().address;
    if (!address) {
      vault.ownedByConnectedSigner = undefined;
      return;
    }

    try {
      vault.ownedByConnectedSigner = await lastValueFrom(
        this.httpClient.get<boolean>(
          `${this.getBaseUrl()}/is-owner?address=${address}&vault=${
            vault.address
          }`,
        ),
      );
    } catch (e) {
      vault.ownedByConnectedSigner = undefined;
    }
  }

  private async setOwner(vault: EnzymeVault): Promise<void> {
    vault.owner = await this.getVaultOwner();
  }

  private async setCanConnectedSignerManageAssets(
    vault: EnzymeVault,
  ): Promise<void> {
    const address = this.signerRepository.store.getValue().address;
    if (!address) {
      vault.canConnectedSignerManageAssets = undefined;
      return;
    }

    try {
      vault.canConnectedSignerManageAssets = await lastValueFrom(
        this.httpClient.get<boolean>(
          `${this.getBaseUrl()}/can-manage-assets?address=${address}&vault=${
            vault.address
          }`,
        ),
      );
    } catch (e) {
      vault.canConnectedSignerManageAssets = undefined;
    }
  }

  private prefillBuyEndSellEntries(assets: EnzymeAsset[]): void {
    const buyEntries: BuyEntry[] = [];
    const sellEntries: SellEntry[] = [];

    assets.forEach(asset => {
      if (asset.isSellable) {
        sellEntries.push(
          new SellEntry(
            asset,
            EthUnitUtil.convertBigIntToBigDecimal(
              asset.balance,
              asset.token.decimals,
            ).getValue(),
            asset.trackedAllocation,
          ),
        );

        if (asset.isTrackedAsset) {
          // use allocation instead of trackedAllocation since trackedAllocation
          // doesn't always sum up to 100%
          buyEntries.push(new BuyEntry(asset, asset.allocation));
        }
      }
    });

    this.rebalancingRepository.setBuyEntries(buyEntries, {
      setEqualProportions: false,
      determineInputStep: false,
    });
    this.rebalancingRepository.setAutoAdjustAllocations(false);
    this.rebalancingRepository.setSellEntries(sellEntries, {
      determineInputStep: false,
    });
    this.rebalancingRepository.setStep(RebalancingStep.INPUT_WEIGHTS);
  }

  public async getVaultOwner(): Promise<Address | undefined> {
    const vault = this.enzymeRepository.store.getValue().selectedVault;

    if (!vault) {
      return undefined;
    }

    if (vault.ownedByConnectedSigner) {
      return this.signerRepository.store.getValue().address;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return lastValueFrom(
        this.httpClient.get<Address>(
          `${this.getBaseUrl()}/owner?vault=${vault.address}`,
          {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            responseType: 'text',
          },
        ),
      );
    } catch (e) {
      console.log(e);
      return undefined;
    }
  }
}
